import AsyncStorage from '@react-native-async-storage/async-storage';
import * as SecureStore from 'expo-secure-store';

// Simple storage wrapper:
// - use SecureStore for sensitive values (tokens, secrets)
// - use AsyncStorage for general cached data

export async function setItem(key: string, value: any): Promise<void> {
  try {
    const s = typeof value === 'string' ? value : JSON.stringify(value);
    await AsyncStorage.setItem(key, s);
  } catch (e) {
    console.warn('setItem error', key, e);
    throw e;
  }
}

export async function getItem<T = any>(key: string): Promise<T | null> {
  try {
    const v = await AsyncStorage.getItem(key);
    if (v == null) return null;
    try {
      return JSON.parse(v) as T;
    } catch (_) {
      return v as unknown as T;
    }
  } catch (e) {
    console.warn('getItem error', key, e);
    throw e;
  }
}

export async function removeItem(key: string): Promise<void> {
  try {
    await AsyncStorage.removeItem(key);
  } catch (e) {
    console.warn('removeItem error', key, e);
    throw e;
  }
}

// SecureStore helpers (for tokens, secrets)
let _secureStoreAvailable: boolean | null = null;

async function isSecureStoreAvailable(): Promise<boolean> {
  if (_secureStoreAvailable != null) return _secureStoreAvailable;
  try {
    const fn = (SecureStore as any).isAvailableAsync;
    if (typeof fn === 'function') {
      _secureStoreAvailable = !!(await fn());
    } else {
      // If API isn't present, assume unavailable (e.g. web)
      _secureStoreAvailable = false;
    }
  } catch {
    _secureStoreAvailable = false;
  }
  return _secureStoreAvailable;
}

const SECURE_FALLBACK_PREFIX = '__secure__:';

export async function setSecureItem(key: string, value: string): Promise<void> {
  try {
    if (await isSecureStoreAvailable()) {
      await SecureStore.setItemAsync(key, value, { keychainAccessible: SecureStore.WHEN_UNLOCKED });
      return;
    }
    // Web fallback: store in AsyncStorage (shimmed to localStorage on web)
    await AsyncStorage.setItem(`${SECURE_FALLBACK_PREFIX}${key}`, value);
  } catch (e) {
    console.warn('setSecureItem error', key, e);
    // Don't hard-fail auth flows on platforms where SecureStore is flaky.
    // Worst case: caller can still rely on HttpOnly cookies for web.
  }
}

export async function getSecureItem(key: string): Promise<string | null> {
  try {
    if (await isSecureStoreAvailable()) {
      return await SecureStore.getItemAsync(key);
    }
    return await AsyncStorage.getItem(`${SECURE_FALLBACK_PREFIX}${key}`);
  } catch (e) {
    console.warn('getSecureItem error', key, e);
    return null;
  }
}

export async function deleteSecureItem(key: string): Promise<void> {
  try {
    if (await isSecureStoreAvailable()) {
      await SecureStore.deleteItemAsync(key);
      return;
    }
    await AsyncStorage.removeItem(`${SECURE_FALLBACK_PREFIX}${key}`);
  } catch (e) {
    console.warn('deleteSecureItem error', key, e);
    // ignore
  }
}

// Convenience helpers for auth token
export const AUTH_TOKEN_KEY = 'kakeibo_auth_token';

export async function saveAuthToken(token: string) {
  return setSecureItem(AUTH_TOKEN_KEY, token);
}

export async function loadAuthToken(): Promise<string | null> {
  return getSecureItem(AUTH_TOKEN_KEY);
}

export async function clearAuthToken() {
  return deleteSecureItem(AUTH_TOKEN_KEY);
}

export default {
  setItem,
  getItem,
  removeItem,
  setSecureItem,
  getSecureItem,
  deleteSecureItem,
  saveAuthToken,
  loadAuthToken,
  clearAuthToken,
};
